import React, { useContext, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getUserAccountStatuses } from '../services/http/ChampionGraphQlQueries';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';
import ChampionConsentForm from '../components/champions/ChampionConsentForm';

const TITLE = 'Champions Consent | Atlassian Community';
const DESCRIPTION = 'Champions consent page.';
const CHAMPIONS_CONSENT_PATH = '/champions/consent';

const getConsentRedirectUrl = () => `${process.env.HOME_BASE_URL}${CHAMPIONS_CONSENT_PATH}`;

const parseUserName = (userName: string): { firstName: string; lastName: string } => {
  const parts = userName.trim() ? userName.trim().split(/\s+/) : [];
  if (parts.length === 1) {
    return { firstName: '', lastName: parts[0] };
  }
  if (parts.length > 1) {
    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts[parts.length - 1],
    };
  }
  return { firstName: '', lastName: '' };
};

type AccountInfo = {
  emailId: string;
};

export const ChampionsConsentPage: React.FC = () => {
  const { user, userIsInitialized } = useContext(UserContext) ?? {};
  const isLoggedIn = !!user;
  const isAuthSignupEnabled = championsAuthSignupIsActive();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [accountError, setAccountError] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const homepage =
    process.env.REACT_APP_ENVI === 'local' ? 'http://localhost:9000' : process.env.HOME_BASE_URL;

  useEffect(() => {
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }
    if (!userIsInitialized || !isLoggedIn) {
      window.location.href = getCommunityLoginUrl(
        { redirectUrl: getConsentRedirectUrl(), experience: 'entry-auth' },
        getEnvironmentAsEnum(),
      );
      return;
    }
    if (!user?.account_id) {
      setAccountError(true);
      setAccountLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAccountInfo = async () => {
      try {
        const response = await getUserAccountStatuses(user.account_id, homepage, homepage);
        const account = response?.data?.unified?.account;
        if (
          cancelled ||
          !account ||
          'code' in account ||
          !account.emailId
        ) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        setAccountInfo({ emailId: account.emailId });
        setAccountLoading(false);
      } catch {
        if (!cancelled) {
          setAccountError(true);
          setAccountLoading(false);
        }
      }
    };
    fetchAccountInfo();
    return () => {
      cancelled = true;
    };
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, user?.account_id, homepage]);

  const canShowContent = isAuthSignupEnabled && userIsInitialized && isLoggedIn;

  if (!canShowContent || accountLoading) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  if (accountError || !accountInfo) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={errorBannerStyles}>Oops! Something went wrong.</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithAnalytics eventScreenName="championsConsentScreen">
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div style={{ width: '100%' }}>
          <ChampionConsentForm
            {...parseUserName(user?.name ?? '')}
            emailId={accountInfo.emailId}
          />
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsConsentPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.400'),
  paddingBottom: token('space.800'),
  minHeight: '400px',
});

const errorBannerStyles = css({
  color: '#3e3e3c',
  fontSize: '18px',
  fontWeight: 400,
  textAlign: 'center',
  lineHeight: '24px',
});
